import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { sampleDefault, sampleImages, SamplesContext, SamplesResponseType, UpdateSamplesContext } from "../../context/samplesContext"
import { buildMeasurementPath, formatDate, formatOrdinal, getSampleIdFromSearch } from "../utils/helper"
import checkboxIcon from '../../assets/images/icons/checked_checkbox.svg'
import uncheckIcon from '../../assets/images/icons/uncheck_checkbox.svg'
import export_default from "../../assets/images/icons/export-default.svg"
import download_default from "../../assets/images/icons/download-default.svg"
import download_hover from "../../assets/images/icons/download-hover.svg"
import JSZip from 'jszip' // ZIPファイル作成のため
import { saveAs } from 'file-saver' // ZIPファイル保存のため
import InfiniteScroll from "react-infinite-scroll-component"
import ReactLoading from "react-loading"
import { useLocation, useNavigate } from "react-router-dom"
import { ImageWithLoading } from "../../components/image/ImageWithLoading"
import { clientFetch } from "../../utils/clientFetch"

export function EcPhoto() {
  const { idSearchSample } = useContext(UpdateSamplesContext)
  const { sample, rowSample } = useContext(SamplesContext)
  const [sampleId, setSampleId] = useState<string>('')
  const [currentSampleInfo, setCurrentSampleInfo] = useState<SamplesResponseType>(sampleDefault)
  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([])

  const navigate = useNavigate()
  const location = useLocation()
  const sampleIdFromSearch = getSampleIdFromSearch(location.search)

  useEffect(() => {
    const fetchSampleInfo = async () => {
      if (!sampleId) {
        setCurrentSampleInfo(sampleDefault)
        setSelectedImageUrls([])
        return
      }
      const result = await idSearchSample(sampleId, false)
      if (result.status === 'success') {
        setCurrentSampleInfo(result.sample) // 新しいstateにサンプル情報をセット
        setSelectedImageUrls([]) // サンプルが切り替わったら選択をリセット
        return
      }
      setCurrentSampleInfo(sampleDefault)
      setSelectedImageUrls([])
    }
    fetchSampleInfo()
  }, [sampleId, idSearchSample, setCurrentSampleInfo])

  const handleBulkDownload = useCallback(async () => {
    if (selectedImageUrls.length === 0) {
      alert('ダウンロードする画像を選択してください。')
      return
    }

    const data = new Date().toLocaleDateString('sv-SE')
    const folderName = currentSampleInfo.sample_number ? `${data}_${currentSampleInfo.sample_number}` : currentSampleInfo.part_number ? `${data}_${currentSampleInfo.part_number}` : `${data}`
    const zip = new JSZip()
    const promises = selectedImageUrls.map(async (url) => {
      try {
        const response = await clientFetch(url)
        if (!response.ok) throw new Error(`Failed to fetch ${url}`)
        const blob = await response.blob()
        const filename = url.substring(url.lastIndexOf('/') + 1)
        zip.file(filename, blob)
      } catch (error) {
        console.error('Error fetching image for zipping', error)
      }
    })

    await Promise.all(promises)

    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${folderName}.zip`)
    })
  }, [selectedImageUrls, currentSampleInfo])

  return (
    <div className="flex flex-col">
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-[3px] bg-black mr-[5px]' ></div>
        <span className='text-[#3E3E3E] text-[18px]'>
          EC PHOTO
        </span>
      </div>
      <div className="flex justify-center max-laptop-sm:flex-col">
        <EcTable setSampleId={setSampleId} />
        <EcImages ecImages={currentSampleInfo.sampleEcImages || []} selectedImageUrls={selectedImageUrls} setSelectedImageUrls={setSelectedImageUrls} />
      </div>
      <div className="w-full flex justify-end mt-2.5 pt-[24px] pr-[10px] space-x-3.5">
        <button onClick={() => navigate(buildMeasurementPath(sampleIdFromSearch || rowSample.id || sample.id))} className="bg-black w-[280px] h-[47px] rounded-[50px]">
          <span className="text-white">検寸画面に戻る</span>
        </button>
        <button onClick={handleBulkDownload} disabled={selectedImageUrls.length === 0} className="bg-[#C5C7FF] w-[170px] h-[47px] rounded-[50px] text-[#545899] font-semibold hover:bg-[#545899] hover:text-white transition-colors disabled:cursor-not-allowed">
          <span>ダウンロード</span>
        </button>
      </div>
    </div>
  )
}

const EC_TABLE_SCROLL_ID = "ec-table-scroll-container"

function EcTable(props: { setSampleId: (id: string) => void }) {
  const { setSampleId } = props
  const { samples, samplesMeta } = useContext(SamplesContext)
  const { loadMoreSamples } = useContext(UpdateSamplesContext)

  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortedSamples, setSortedSamples] = useState(samples)

  useEffect(() => {
    if (Array.isArray(samples)) {
      const sorted = samples.slice().sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        if (sortOrder === 'asc') {
          return dateA - dateB // 昇順でソート
        }
        return dateB - dateA // 降順でソート
      })
      setSortedSamples(sorted)
    }
  }, [samples, sortOrder])

  return (
    <div className="pt-5 px-[27px] pb-[22px] min-w-[651px] min-h-[453px] bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] rounded-[20px] laptop-md:min-h-[740px] max-laptop-sm:shadow-none max-laptop-sm:bg-[#F9F9F9] max-laptop-sm:px-0">
      <div className="flex items-center justify-end space-x-[8px] mb-[10px]">
        <label className="flex items-center space-x-[8px] ">
          <input
            type="radio"
            name="sort"
            value="asc"
            checked={sortOrder === 'asc'}
            onChange={() => setSortOrder('asc')}
            className="w-[26px] h-[26px] accent-[#545899]"
          />
          <span>
            撮影日順(昇順)
          </span>
        </label>
        <label className="flex items-center space-x-[8px]">
          <input
            type="radio"
            name="sort"
            value="desc"
            checked={sortOrder === 'desc'}
            onChange={() => setSortOrder('desc')}
            className="w-[26px] h-[26px] accent-[#545899]"
          />
          <span>
            撮影日順(降順)
          </span>
        </label>
      </div>
      <div className='max-h-[398px] bg-white border min-h-[398px] h-full overflow-y-scroll laptop-sm:min-h-[650px] laptop-md:h-[650px]' id={EC_TABLE_SCROLL_ID}>
        <InfiniteScroll
          dataLength={sortedSamples?.length ?? 0}
          next={loadMoreSamples}
          hasMore={samplesMeta.hasMore}
          loader={
            <div className='flex items-center justify-center py-3 text-center text-xs text-[#666666]'>
              <ReactLoading type='spin' color='#666666' height={16} width={16} className='inline-block mr-2' />
              <span>サンプル一覧を読み込み中です…</span>
            </div>
          }
          endMessage={<div className='py-3 text-center text-xs text-[#666666]'>表示可能なサンプルはすべて読み込み済みです。</div>}
          scrollableTarget={EC_TABLE_SCROLL_ID}
          scrollThreshold='200px'
          style={{ overflow: 'visible' }}
        >
          <table className='bg-white w-full h-full text-[#666666]'>
            <thead className='sticky top-0'>
              <tr className='[&>th]:bg-[#3E3E3E] [&>th]:text-white [&>th]:font-normal [&>th]:text-[14px] [&>th]:border'>
                <th className='sticky top-0 w-[88px]'></th>
                <th className='sticky top-0 w-[145px]'>検寸日時</th>
                <th className='sticky top-0 w-[145px]'>品番</th>
                <th className='sticky top-0 w-[173px]'>サンプルNo</th>
                <th className='sticky top-0 w-[173px]'>サンプル状況</th>
                <th className='sticky top-0 w-[100px]'>サイズ</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(samples) && sortedSamples?.map((sample, index) => (
                <tr key={index}>
                  <td className='border'>
                    <div className='flex items-center justify-center w-full h-full'>
                      <label className='cursor-pointer'>
                        <input
                          type="radio"
                          name='check'
                          className='hidden peer'
                          onChange={() => setSampleId(sample.id)}
                        />
                        <img src={uncheckIcon} alt='uncheck' className='block peer-checked:hidden' />
                        <img src={checkboxIcon} alt='checked' className='hidden peer-checked:block' />
                      </label>
                    </div>
                  </td>
                  <td className='border text-[14px] text-center'>{formatDate(sample.created_at)}</td>
                  <td className='border text-[14px] text-center'>{sample.part_number || '-'}</td>
                  <td className='border text-[14px] text-center'>{sample.sample_number || '-'}</td>
                  <td className='border text-[14px] text-center'>{sample.kind === 'final' ? 'FINAL' : sample.time !== 0 ? formatOrdinal(sample.time) : '-'}</td>
                  <td className='border text-[16px] text-center'>{sample.size || '-'}</td>
                </tr>
              ))}
              <tr>
                <td className='border h-full'></td>
                <td className='border'></td>
                <td className='border'></td>
                <td className='border'></td>
                <td className='border-b'></td>
                <td className='border-x border-b'></td>
              </tr>
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}

interface EcImagesProps {
  ecImages: sampleImages[];
  selectedImageUrls: string[];
  setSelectedImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
}

function EcImages({ ecImages, selectedImageUrls, setSelectedImageUrls }: EcImagesProps) {
  const sortedEcImages = useMemo(
    () => [...ecImages].sort((a, b) => a.image_index - b.image_index),
    [ecImages]
  )
  const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(null)

  const handleCheckboxChange = (url: string) => {
    setSelectedImageUrls(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    )
  }

  const handleDownload = useCallback(async (url: string) => {
    try {
      const response = await clientFetch(url)
      if (!response.ok) throw new Error('ダウンロード失敗')
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = url.substring(url.lastIndexOf('/') + 1) // URLからファイル名を抽出
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl) // メモリ解放
    } catch (error) {
      console.error('DOWNLOAD ERROR', error)
    }
  }, [])

  return (
    <div className="w-full h-full ml-[10px] max-w-[1600px] max-laptop-sm:ml-0">
      <div className="relative h-[490px] overflow-y-auto desktop-sm:min-h-[650px] desktop-sm:h-[650px] pr-2">
        <div className="grid grid-cols-2 gap-[10px] h-full max-laptop-sm:grid-cols-3 laptop-md:grid-cols-3 desktop-sm:grid-cols-4 desktop-sm:grid-rows-3 desktop-xxl:grid-cols-5 desktop-xxxl:grid-cols-6">
          {sortedEcImages.map((ec, index) => (
            <div key={index} className="relative max-w-full max-h-[215px] bg-[#F3F3F3] border rounded-[10px] overflow-hidden">
              <div className="min-h-[46px] w-full flex items-center justify-between px-5 border-b border-[#000]">
                <label className="cursor-pointer">
                  <input
                    type="checkbox"
                    className="hidden peer"
                    checked={selectedImageUrls.includes(ec.url)}
                    onChange={() => handleCheckboxChange(ec.url)}
                  />
                  <img
                    src={export_default}
                    alt="unchecked"
                    className="block peer-checked:hidden w-6 h-6"
                  />
                  <img
                    src={checkboxIcon}
                    alt="checked"
                    className="hidden peer-checked:block w-6 h-6"
                  />
                </label>
                <button
                  onClick={() => handleDownload(ec.url)}
                  onMouseEnter={() => setHoveredImageIndex(index)}
                  onMouseLeave={() => setHoveredImageIndex(null)}
                >
                  <img
                    src={hoveredImageIndex === index ? download_hover : download_default}
                    alt="download"
                    className="w-6 h-6"
                  />
                </button>
              </div>
              <div className="h-[169px] bg-white flex items-center justify-center overflow-hidden relative">
                <ImageWithLoading
                  src={ec.thumbnail_url}
                  alt={`ecimage${index + 1}`}
                  loadingFallback={
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
                      <ReactLoading type="spin" color="#545899" width={32} height={32} />
                    </div>
                  }
                  className="object-contain max-w-full max-h-full transition-opacity duration-200"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
